import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { AgreeScale, BackButton, BinaryChoice, FrequencySlider, PickOne } from "../components/Choices";
import { Loading, Message, Page } from "../components/Page";
import { Paywall } from "../components/Paywall";
import { QuadrantGlyph } from "../components/ResultMap";
import { SectionProgress } from "../components/SectionProgress";
import { TriadPicker, isTriadComplete, type TriadDraft } from "../components/TriadPicker";
import { ApiError, api } from "../lib/api";
import { TRIAD_COPY } from "../lib/copy";
import { readbackParts } from "../lib/readback";
import { sectionInfo, sectionProgress } from "../lib/sections";
import { isTriadChoice, type Choice, type ChoiceOption, type PublicItem, type ScaleType, type TakeView } from "../lib/types";
import { useAutosave } from "../lib/useAutosave";
import { SaveStatus } from "../components/SaveStatus";
import { useDwell } from "../lib/useDwell";

/** Delay before a Yes/No or True/False pick moves on. */
export const AUTO_ADVANCE_MS = 250;

/** A triad (scoring v4, Part 4) is answered only with both picks; any other answer counts once it exists. */
const isAnswered = (v: Choice | undefined): boolean => v !== undefined && (typeof v !== "object" || isTriadChoice(v));

const firstUnanswered = (take: TakeView, answers: Record<string, Choice>) => {
  const i = take.items.findIndex((item) => !isAnswered(answers[item.id]));
  return i < 0 ? take.items.length : i;
};

/** The labelled stops of one answer scale. A version only sends the scales it uses. */
const scaleOf = (take: TakeView, type: ScaleType): ChoiceOption[] => take.scales[type] ?? [];

export function TakeFlow() {
  const { takeId = "" } = useParams();
  const navigate = useNavigate();
  // Always load fresh: history state survives a reload, so a take passed through navigation
  // could be stale and resume at the wrong question.
  const [take, setTake] = useState<TakeView | null>(null);
  const [error, setError] = useState<"not_found" | "failed" | null>(null);
  // Back from Stripe Checkout (the paywall): ?checkout=done&session_id=... or ?checkout=cancelled.
  const [params, setParams] = useSearchParams();
  const [cancelled] = useState(() => params.get("checkout") === "cancelled");
  const [loads, setLoads] = useState(0);

  useEffect(() => {
    setTake(null);
    let live = true;
    const sessionId = params.get("checkout") === "done" ? params.get("session_id") : null;
    // The address alone proves nothing: the API asks Stripe about the session before the questions open.
    const confirmed = sessionId ? api.confirmCheckout(takeId, sessionId).catch(() => undefined) : Promise.resolve();
    if (params.has("checkout")) setParams({}, { replace: true });
    confirmed
      .then(() => api.getTake(takeId))
      .then((t) => live && setTake(t))
      .catch((e) => live && setError(e instanceof ApiError && (e.status === 404 || e.status === 422) ? "not_found" : "failed"));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the checkout params are read once, on arrival
  }, [takeId, loads]);

  useEffect(() => {
    if (take?.status === "completed") navigate(`/results/${take.id}`, { replace: true });
  }, [take, navigate]);

  if (error === "not_found") {
    return (
      <Message title="We can't find this test">
        <p>It may have been started on another device or browser. Sign in with your email and we'll send you a link to pick up where you left off.</p>
        <p className="mt-6 flex flex-wrap gap-x-6 gap-y-3">
          <a href="/sign-in">Sign in with your email</a>
          <a href="/">Start a new one</a>
        </p>
      </Message>
    );
  }
  if (error === "failed") {
    return (
      <Message title="Something went wrong">
        <p>We couldn't load your questions. Check your connection and reload the page.</p>
      </Message>
    );
  }
  if (!take || take.status === "completed") return <Loading label="Loading your questions" />;
  if (take.payment === "due")
    return <Paywall takeId={take.id} firstName={take.firstName} event={take.event ?? null} priceCents={take.priceCents ?? null} codeAtCheckout={take.codeAtCheckout ?? false} cancelled={cancelled} onCheck={() => setLoads((n) => n + 1)} />;
  return <Flow key={take.id} take={take} />;
}

function Flow({ take }: { take: TakeView }) {
  const navigate = useNavigate();
  const total = take.items.length;
  const [answers, setAnswers] = useState<Record<string, Choice>>(take.answers);
  // Triads (scoring v4): picks so far. Only a complete pair is saved or counts as an answer.
  const [drafts, setDrafts] = useState<Record<string, TriadDraft>>({});
  const [index, setIndex] = useState(() => firstUnanswered(take, take.answers));
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const firstRender = useRef(true);

  const goToResults = useCallback(() => navigate(`/results/${take.id}`, { replace: true }), [navigate, take.id]);
  const send = useCallback((code: string, v: Choice) => api.saveResponse(take.id, code, v), [take.id]);
  const autosave = useAutosave<Choice>(send, goToResults);

  const clearTimer = () => {
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    advanceTimer.current = null;
  };
  useEffect(() => clearTimer, []);

  // Move focus to the new question so screen readers read it, and start at the top.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    window.scrollTo(0, 0);
    headingRef.current?.focus();
  }, [index]);

  const item = take.items[index];
  const progress = useMemo(() => sectionProgress(take.items, index), [take.items, index]);
  // A quiet nudge after a long pause on an unanswered question (not the first of a part).
  const itemDone = !item
    ? true
    : item.type === "TRI"
      ? isTriadComplete(drafts[item.id] ?? (isTriadChoice(answers[item.id]) ? (answers[item.id] as TriadDraft) : {}))
      : answers[item.id] !== undefined;
  const firstOfPart = !item || index === 0 || take.items[index - 1]?.section !== item.section;
  const stuck = useDwell(item ? item.id : null, itemDone || firstOfPart);

  const pick = (it: PublicItem, value: Choice, autoAdvance = false) => {
    setAnswers((a) => ({ ...a, [it.id]: value }));
    setNotice(null);
    autosave.save(it.id, value);
    if (autoAdvance) {
      clearTimer();
      const from = index;
      advanceTimer.current = setTimeout(() => {
        advanceTimer.current = null;
        setIndex((i) => (i === from ? Math.min(total, i + 1) : i));
      }, AUTO_ADVANCE_MS);
    }
  };

  const pickTriad = (it: PublicItem, draft: TriadDraft) => {
    setDrafts((d) => ({ ...d, [it.id]: draft }));
    if (isTriadComplete(draft)) pick(it, { most: draft.most, least: draft.least });
    else setNotice(null);
  };

  const next = () => {
    clearTimer();
    setNotice(null);
    setIndex((i) => Math.min(total, i + 1));
  };
  const back = () => {
    clearTimer();
    setNotice(null);
    setIndex((i) => Math.max(0, i - 1));
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.submit(take.id);
      setSubmitted(true);
    } catch (e) {
      if (e instanceof ApiError && e.code === "incomplete") {
        const missing = (e.details as { missing?: string[] } | undefined)?.missing ?? [];
        const at = take.items.findIndex((it) => missing.includes(it.id));
        const target = at >= 0 ? at : firstUnanswered(take, answers);
        if (target < total) {
          setIndex(target);
          setNotice(
            missing.length > 1
              ? `${missing.length} questions still need an answer. Here's the first.`
              : "This question still needs an answer.",
          );
          setSubmitting(false);
          return;
        }
      }
      setSubmitError(
        e instanceof ApiError && e.status === 0
          ? "We couldn't reach the server. Check your connection and try again."
          : e instanceof ApiError && e.code === "link_used"
            ? // The free test from this link was finished in another tab or on another device.
              e.message
            : "Something went wrong finishing your profile. Mind trying again?",
      );
      setSubmitting(false);
    }
  };

  // The last answer is in: submit straight away, so the write-up starts while the finish screen
  // counts down (Edward, Nov 3: "buy us an extra 10 seconds"). Once per arrival; after an error,
  // "Try again" submits. Waits for answers still saving.
  const blocked = autosave.pending || Object.keys(autosave.failed).length > 0;
  const autoSubmitted = useRef(false);
  useEffect(() => {
    if (item) {
      autoSubmitted.current = false;
      return;
    }
    if (blocked || autoSubmitted.current) return;
    autoSubmitted.current = true;
    void submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- submit is rebuilt every render; this runs on arriving at the end
  }, [item, blocked]);

  const status = <SaveStatus autosave={autosave} />;

  if (!item) {
    return (
      <Page right={`${total} / ${total}`} footer={false}>
        <div className="mt-4">
          <SectionProgress sections={progress} />
        </div>
        <Finishing
          total={total}
          headingRef={headingRef}
          submitted={submitted}
          error={submitError}
          retrying={submitting}
          saveStuck={!submitted && !submitting && Object.keys(autosave.failed).length > 0}
          onRetry={submit}
          onBack={back}
          onDone={goToResults}
        />
        {status}
      </Page>
    );
  }

  const value = answers[item.id];
  const section = sectionInfo(item.section);
  const isBinary = item.type === "YN" || item.type === "TF";
  const draft: TriadDraft = drafts[item.id] ?? (isTriadChoice(value) ? value : {});
  const answered = item.type === "TRI" ? isTriadComplete(draft) : value !== undefined;

  return (
    <Page right={`${section.label} · ${index + 1} / ${total}`} footer={false}>
      <div className="mt-4">
        <SectionProgress sections={progress} />
      </div>

      <div className="mt-8 flex flex-1 flex-col" key={item.id}>
        {notice ? (
          <p role="alert" className="mb-4 rounded-[16px] border border-line bg-panel px-4 py-3 text-[15px]">
            {notice}
          </p>
        ) : null}

        <QuestionHeading item={item} take={take} value={value} headingRef={headingRef} number={index + 1} />

        {/* The answers follow the question at a steady gap (32-64px); only Back and Continue stay
            pinned to the bottom, in the same place on every question (Edward, Oct 22: "D"). Pinning
            the answers too left a big empty middle on tall phones and desktops. */}
        <div className="mt-[clamp(2rem,6vh,4rem)] flex flex-1 flex-col">
          {stuck ? (
            <p role="status" className="mb-4 text-center text-[15px] text-muted" data-testid="first-instinct">
              Not sure? Go with your first instinct.
            </p>
          ) : null}
          {item.type === "MC4" ? (
            <PickOne options={item.options} value={value} onPick={(v) => pick(item, v)} />
          ) : item.type === "TRI" ? (
            <TriadPicker options={item.options} value={draft} onChange={(d) => pickTriad(item, d)} />
          ) : item.type === "A4" || item.type === "A5" ? (
            <AgreeScale options={scaleOf(take, item.type)} value={value} onPick={(v) => pick(item, v)} />
          ) : item.type === "F4" ? (
            <>
              <p className="mb-5 text-center text-[14px] text-muted">
                {value === undefined
                  ? "How often is this true? Slide to answer."
                  : "Read it back. Slide again if it's not quite you."}
              </p>
              <FrequencySlider name={`q-${item.id}`} options={scaleOf(take, "F4")} value={value} onPick={(v) => pick(item, v)} />
            </>
          ) : item.type === "F5" ? (
            // v4's how-often questions: big labelled buttons, not v1's slider. Older takers didn't
            // know to tap the bar (Dane, Oct 21 call).
            <AgreeScale options={scaleOf(take, "F5")} value={value} onPick={(v) => pick(item, v)} />
          ) : item.type === "YN" || item.type === "TF" ? (
            <>
              <BinaryChoice options={scaleOf(take, item.type)} value={value} onPick={(v) => pick(item, v, true)} />
              <p className="mt-3 text-center text-[13px] text-muted">Picking one moves you on. You can always go back.</p>
            </>
          ) : (
            <p role="alert" className="text-[15px] text-danger">
              This question isn't supported in this version of the app. Reload the page and try again.
            </p>
          )}

          <div className="mt-auto flex gap-3 pt-6">
            <BackButton onClick={back} disabled={index === 0} />
            {isBinary ? null : (
              <button type="button" className="btn btn-primary flex-1" disabled={!answered} onClick={next}>
                Continue
              </button>
            )}
          </div>
        </div>
      </div>
      {/* Outside the per-question subtree, so the live region persists and "Saved" is announced. */}
      {status}
      {index === 0 ? (
        <p className="text-pretty mt-1 text-center text-[13px] leading-snug text-muted" data-testid="come-back">
          Your answers save as you go. Stop anytime and pick up where you left off at giftsprofile.com/sign-in with your email.
        </p>
      ) : null}
    </Page>
  );
}

/** How long the finish screen shows, at least: the write-up starts at submit and takes ~20 seconds. */
export const FINISH_MS = 10_000;

/** What's being put together, ticked off in turn as the bar fills. The last one only when a write-up is being written. */
const FINISH_STEPS = ["Adding up your answers", "Placing you on the map", "Finding your top gifts"];

/**
 * After the last question (Edward, Nov 3: the old "That's all 38" page was "wasted blank space"):
 * the answers are already submitted, and a bar fills for FINISH_MS before the results open, so
 * the write-up has a head start. Holds near the end if submitting takes longer. On an error, says
 * so with Try again and Back.
 */
function Finishing({
  total,
  headingRef,
  submitted,
  error,
  retrying,
  saveStuck,
  onRetry,
  onBack,
  onDone,
}: {
  total: number;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  submitted: boolean;
  error: string | null;
  retrying: boolean;
  /** An answer hasn't saved yet (still retrying, or it can't): submit waits, so they get a way back to it. */
  saveStuck: boolean;
  onRetry: () => void;
  onBack: () => void;
  onDone: () => void;
}) {
  const [elapsed, setElapsed] = useState(0);
  const [writeups, setWriteups] = useState(false);
  useEffect(() => {
    const since = Date.now();
    const tick = setInterval(() => setElapsed(Date.now() - since), 100);
    return () => clearInterval(tick);
  }, []);
  useEffect(() => {
    let live = true;
    api
      .getConfig()
      .then((c) => live && setWriteups(c.writeupsEnabled === true))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  const timeUp = elapsed >= FINISH_MS;
  useEffect(() => {
    if (submitted && timeUp) onDone();
  }, [submitted, timeUp, onDone]);

  // The dot hops around the map as if it's finding your spot, a little less each hop, leaving a
  // faint trail, and settles in the middle near the end (Edward, Nov 3). Still for reduced motion.
  const [spot, setSpot] = useState<{ at: { x: number; y: number }; trail: { x: number; y: number }[] }>({ at: { x: 0, y: 0 }, trail: [] });
  const settle = elapsed >= FINISH_MS * 0.85;
  useEffect(() => {
    if (settle || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setSpot((s) => ({ at: { x: 0, y: 0 }, trail: s.trail.slice(1) }));
      return;
    }
    const since = Date.now();
    const hop = setInterval(() => {
      const reach = 44 * (1 - Math.min(0.6, (Date.now() - since) / FINISH_MS));
      const next = { x: Math.round((Math.random() * 2 - 1) * reach), y: Math.round((Math.random() * 2 - 1) * reach) };
      setSpot((s) => ({ at: next, trail: [...s.trail, s.at].slice(-3) }));
    }, 750);
    return () => clearInterval(hop);
  }, [settle]);

  const steps = [...FINISH_STEPS, writeups ? "Writing your personal summary" : "Putting it all together"];
  const current = Math.min(steps.length - 1, Math.floor((elapsed / FINISH_MS) * steps.length));
  // Holds at 95% until the submit is in.
  const filled = Math.min(elapsed / FINISH_MS, submitted ? 1 : 0.95);

  return (
    <div className="mt-14 flex flex-1 flex-col">
      <div className={error ? undefined : "finish-glyph"}>
        <QuadrantGlyph at={error ? undefined : spot.at} trail={error ? [] : spot.trail} />
      </div>
      <h1 ref={headingRef} tabIndex={-1} className="heading mt-9 text-balance text-[32px] leading-[1.1] outline-none">
        That's all {total}. Nice work!
      </h1>
      {error ? (
        <>
          <p role="alert" className="mt-4 text-[17px] text-danger">
            {error}
          </p>
          <div className="mt-auto flex gap-3 pt-10">
            <BackButton onClick={onBack} />
            <button type="button" className="btn btn-primary flex-1" disabled={retrying} onClick={onRetry}>
              {retrying ? "One moment…" : "Try again"}
            </button>
          </div>
        </>
      ) : (
        <>
          {saveStuck ? (
            // Nothing is being built while an answer hasn't saved (submit waits for it): say so, with
            // Back to that answer; the Retry is in the save status below (review of #86).
            <div className="mt-3 flex flex-col gap-4" data-testid="finish-save-stuck">
              <p role="status" className="text-pretty text-[17px] leading-relaxed">
                Hang on, one of your answers hasn't saved yet. We'll keep trying, or you can go back and check it.
              </p>
              <div>
                <BackButton onClick={onBack} />
              </div>
            </div>
          ) : (
            <p className="mt-3 text-pretty text-[17px] leading-relaxed text-muted">
              We're building your Gifts Profile now. It'll be ready in just a few seconds.
            </p>
          )}
          <div className="mt-10" data-testid="finish-progress" hidden={saveStuck}>
            <div
              className="h-2 overflow-hidden rounded-full bg-line"
              role="progressbar"
              aria-label="Building your profile"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(filled * 100)}
            >
              <div className="finish-bar h-full rounded-full bg-accent-text transition-[width] duration-100 ease-linear" style={{ width: `${filled * 100}%` }} />
            </div>
            <ol className="mt-6 flex flex-col gap-3.5" aria-label="What we're putting together">
              {steps.map((label, i) => {
                const done = i < current || (submitted && timeUp);
                const now = i === current && !done;
                return (
                  <li key={label} className={`flex items-center gap-3 text-[16px] ${done || now ? "text-ink" : "text-muted"}`}>
                    {done ? (
                      <span aria-hidden="true" className="grid size-6 shrink-0 place-items-center rounded-full bg-accent-text text-panel">
                        <svg viewBox="0 0 16 16" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3.5 8.5l3 3 6-7" />
                        </svg>
                      </span>
                    ) : (
                      <span aria-hidden="true" className={`grid size-6 shrink-0 place-items-center rounded-full border-2 ${now ? "finish-ring border-accent-text" : "border-line"}`}>
                        {now ? <span className="finish-now size-2 rounded-full bg-accent-text" /> : null}
                      </span>
                    )}
                    <span className={now ? "font-semibold" : undefined}>
                      {label}
                      {now ? "…" : ""}
                      <span className="sr-only">{done ? ", done" : now ? ", in progress" : ""}</span>
                    </span>
                  </li>
                );
              })}
            </ol>
          </div>
        </>
      )}
    </div>
  );
}

/** The small line above each question (frequency has its own hint by the slider). */
const KICKERS: Partial<Record<PublicItem["type"], string>> = {
  A4: "How much do you agree?",
  YN: "Yes or no",
  TF: "True or false",
};

function QuestionHeading({
  item,
  take,
  value,
  number,
  headingRef,
}: {
  item: PublicItem;
  take: TakeView;
  value: Choice | undefined;
  number: number;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}) {
  const numeral = (
    <span aria-hidden="true" className="numeral block text-[34px] text-accent-text">
      {String(number).padStart(2, "0")}
    </span>
  );

  // Scoring v4 sends a title for each part ("How you see yourself"), which replaces the per-type kicker.
  const part = take.parts?.[item.section];

  if (item.type === "MC4") {
    return (
      <div>
        {numeral}
        <p className="kicker mt-2">{part ?? "Pick the one that's most you"}</p>
        <h1 ref={headingRef} tabIndex={-1} className="heading mt-2 text-[25px] leading-[1.18] outline-none sm:text-[28px]">
          <span className="sr-only">Question {number}. </span>
          {item.prompt}
        </h1>
      </div>
    );
  }
  if (item.type === "TRI") {
    return (
      <div>
        {numeral}
        {part ? <p className="kicker mt-2">{part}</p> : null}
        <h1
          ref={headingRef}
          tabIndex={-1}
          data-testid="statement"
          className="heading mt-2 text-[28px] leading-[1.15] outline-none"
        >
          <span className="sr-only">Question {number}. </span>
          {TRIAD_COPY.heading}
        </h1>
      </div>
    );
  }
  const kicker = part ?? KICKERS[item.type];

  let statement: React.ReactNode = item.text;
  // Scoring v4 frequency items put their lead-in ("In the recent memory, how often did you…") above the statement.
  if (item.lead) {
    statement = (
      <>
        <span className="mb-2 block font-sans text-[17px] leading-snug text-muted">{item.lead}</span>{" "}
        {item.text}
      </>
    );
  }
  let chip: string | null = null;
  if (item.type === "F4" && value !== undefined) {
    const label = scaleOf(take, "F4").find((o) => o.value === value)?.label ?? "";
    const parts = readbackParts(item.id, item.text, label);
    if (parts) {
      statement = (
        <>
          {parts.before}
          <span className="readback-word">{parts.word}</span>
          {parts.after}
        </>
      );
    } else {
      chip = label.toLowerCase();
    }
  }

  return (
    <div>
      {numeral}
      {kicker ? <p className="kicker mt-2">{kicker}</p> : null}
      <h1
        ref={headingRef}
        tabIndex={-1}
        data-testid="statement"
        className="heading mt-2 text-[25px] leading-[1.18] outline-none sm:text-[28px]"
      >
        <span className="sr-only">Question {number}. </span>
        {statement}
      </h1>
      {chip ? (
        <p className="mt-4">
          <span
            data-testid="readback-chip"
            className="serif inline-flex min-h-9 items-center rounded-full border border-accent bg-soft px-4 text-[18px]"
          >
            {chip}
          </span>
        </p>
      ) : null}
    </div>
  );
}
